import { ICatch, ICatchRaw, TCatchSummary } from '@ftd/types'

import { BASE_URL } from '@/api/constants'
import { secureFetch } from '@/api/secure-fetch'

export type TGetSessionCatchesResponse = {
  docs: ICatchRaw[]
  limit: number
  skip: number
  total: number
}

export const apiCreateCatch = async (sessionId: string, data: Partial<ICatch<string>>): Promise<ICatchRaw> => {
  const res = await secureFetch(`${BASE_URL}/catches/sessions/${sessionId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to create catch')
  return res.json()
}

export const apiGetSessionCatches = async (
  sessionId: string,
  skip: number,
  limit: number,
): Promise<TGetSessionCatchesResponse> => {
  const res = await secureFetch(`${BASE_URL}/catches/sessions/${sessionId}?skip=${skip}&limit=${limit}`, {
    next: { revalidate: 0 },
  })
  if (!res.ok) throw new Error('Failed to fetch catches')
  return res.json()
}

export const apiUpdateCatch = async (
  sessionId: string,
  catchId: string,
  data: Partial<ICatch<string>>,
): Promise<ICatchRaw> => {
  const res = await secureFetch(`${BASE_URL}/catches/sessions/${sessionId}/${catchId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
  if (!res.ok) throw new Error('Failed to update catch')
  return res.json()
}

export const apiDeleteCatch = async (sessionId: string, catchId: string): Promise<void> => {
  const res = await secureFetch(`${BASE_URL}/catches/sessions/${sessionId}/${catchId}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to delete catch')
  return res.json()
}

export const apiDuplicateCatch = async (sessionId: string, catchId: string): Promise<ICatch> => {
  const res = await secureFetch(`${BASE_URL}/sessions/${sessionId}/catches/${catchId}/duplicate`, {
    method: 'POST',
  })
  if (!res.ok) throw new Error('Failed to duplicate catch')
  return res.json()
}

export const apiGetCatch = async (catchId: string): Promise<ICatchRaw> => {
  const res = await secureFetch(`${BASE_URL}/catches/${catchId}`, {
    next: { revalidate: 0 },
  })
  if (!res.ok) throw new Error('Failed to fetch catch')
  return res.json()
}

export const apiGetCatchFieldValues = async (field: string, search?: string): Promise<string[]> => {
  const searchParams = new URLSearchParams()
  if (search) searchParams.append('search', search)

  const res = await secureFetch(`${BASE_URL}/catches/fields/${field}?${searchParams.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch catch field values')
  const data = await res.json()
  return data.data
}

export const apiGetCatchSummary = async (sessionIds: string[]): Promise<TCatchSummary> => {
  const searchParams = new URLSearchParams()
  sessionIds.forEach((id) => searchParams.append('sessionIds[]', id))

  const res = await secureFetch(`${BASE_URL}/catches/summary?${searchParams.toString()}`)
  if (!res.ok) throw new Error('Failed to fetch catch summary')
  return res.json()
}
